import React from 'react';
import './ProductCard.css';

export interface ProductCardProduct {
  id: number;
  name: string;
  brand?: string;
  form?: string;
  category?: string;
  sizeLbs?: string;
  sizeUnit?: string;
  price: string;
  isByoc?: boolean;
}

interface ProductCardProps {
  product: ProductCardProduct;
  onAddToCart: () => void;
  adding?: boolean;
  disabled?: boolean;
}

/**
 * Strip brand prefix and size suffix from product name for cleaner display.
 *
 * Examples:
 *   "5 STAR Layer Mash Winter (Nov-Apr)" → "Layer Mash Winter (Nov-Apr)"
 *   "5 STAR Alfalfa Pellets 40lb bag" → "Alfalfa Pellets"
 *   "Five Star All Flock Mash" → "All Flock Mash"
 */
function cleanProductName(name: string, brand?: string): string {
  let cleaned = name;

  // Strip brand prefix patterns
  const prefixes = ['5 STAR', 'FIVE STAR', '5 Star', 'Five Star'];
  if (brand) {
    prefixes.push(brand, brand.toUpperCase());
  }
  for (const prefix of prefixes) {
    if (cleaned.toUpperCase().startsWith(prefix.toUpperCase())) {
      cleaned = cleaned.slice(prefix.length).trim();
      // Remove leading dash or colon
      cleaned = cleaned.replace(/^[-:]\s*/, '');
      break;
    }
  }

  // Strip size suffix like "40lb bag", "50lb Bag", "40 lb bag"
  cleaned = cleaned.replace(/\s+\d+\s*lbs?\s*(bag)?$/i, '').trim();

  return cleaned || name;
}

const FORM_COLORS: Record<string, { bg: string; text: string }> = {
  PELLET: { bg: '#e8f0d8', text: '#4a5e2a' },
  Pellet: { bg: '#e8f0d8', text: '#4a5e2a' },
  MASH: { bg: '#f5ead0', text: '#7a5c2e' },
  Mash: { bg: '#f5ead0', text: '#7a5c2e' },
  CRUMBLE: { bg: '#e5dfd6', text: '#5e5040' },
  Crumble: { bg: '#e5dfd6', text: '#5e5040' },
};

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart, adding, disabled }) => {
  const displayName = cleanProductName(product.name, product.brand);
  const formKey = product.form || '';
  const formColor = FORM_COLORS[formKey] || FORM_COLORS['Mash'];
  const showForm = formKey && !product.isByoc;

  // Format price to always show 2 decimal places
  const priceNum = parseFloat(product.price);
  const formattedPrice = isNaN(priceNum) ? product.price : priceNum.toFixed(2);

  // Format size
  const sizeDisplay = product.sizeLbs && product.sizeLbs !== '0'
    ? `${product.sizeLbs} ${product.sizeUnit || 'lb'} bag`
    : '';

  return (
    <div className="pcard">
      <div className="pcard-top-row">
        {product.brand && (
          <span className="pcard-brand">{product.brand}</span>
        )}
        {showForm && (
          <span
            className="pcard-form-badge"
            style={{ background: formColor.bg, color: formColor.text }}
          >
            {formKey.toUpperCase()}
          </span>
        )}
      </div>

      <div className="pcard-body">
        <h3 className="pcard-name">{displayName}</h3>
        {sizeDisplay && <span className="pcard-size">{sizeDisplay}</span>}
      </div>

      <div className="pcard-bottom">
        <div className="pcard-price">${formattedPrice}</div>
        <button
          className="pcard-add-btn"
          onClick={onAddToCart}
          disabled={adding || disabled}
        >
          {adding ? 'Adding...' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
};

export { cleanProductName };
export default ProductCard;
